import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@app/data/common-imports';
import { AuthenticationService } from './authentication.service';
import { DesktopUpdateRuntimeService } from './desktop-update-runtime.service';
import { NodeService } from './node.service';
import { RecorderRuntimeService } from './recorder-runtime.service';

/**
 * Starts desktop integration at application scope. Dashboard data may be
 * temporarily unavailable during a backend restart, but that must not prevent
 * the native recorder from attaching to the reload-proof Electron host. A
 * failed IPC attachment is retried without requiring another page reload.
 */
@Injectable({ providedIn: 'root' })
export class DesktopIntegrationBootstrapService implements OnDestroy {
    private started = false;
    private retryTimer: any = null;

    constructor(
        private readonly authentication: AuthenticationService,
        private readonly desktopUpdates: DesktopUpdateRuntimeService,
        private readonly node: NodeService,
        private readonly recorder: RecorderRuntimeService
    ) { }

    start(): void {
        if (this.started || !this.node.isAvailable() || !this.authentication.isAuthenticated()) return;
        const user = this.authentication.getAuthenticatedUser();
        if (!user?.id) return;

        this.started = true;
        this.recorder.start({
            userId: user.id,
            isPro: user.isProPlan(),
            localBackend: window.location.hostname.includes('localhost')
        }).then(() => {
            this.desktopUpdates.start();
        }).catch(error => {
            this.started = false;
            console.warn('Desktop integration startup failed; retrying:', error);
            // The main process may still be completing its native startup while
            // a freshly rebuilt renderer is already bootstrapping.
            this.retryTimer ??= setTimeout(() => {
                this.retryTimer = null;
                this.start();
            }, environment.production ? 5000 : 1500);
        });
    }

    ngOnDestroy(): void {
        if (this.retryTimer) clearTimeout(this.retryTimer);
        this.retryTimer = null;
    }
}
